"use client";

import { DATA } from "@/common/data";
import { misc, mobilePagePadding, util } from "@/common/theme";
import emitter from "@/mono/lib/emitter";
import useIsomorphicLayoutEffect from "@/mono/lib/hooks/useIsomorphicLayoutEffect";
import { useTicker } from "@/mono/lib/hooks/useRaf";
import { useScrollRigScrollTrigger } from "@/mono/lib/hooks/useScrollRigScrollTrigger";
import { lerp } from "@/mono/lib/math";
import { mediaMobile } from "@/mono/lib/responsive";
import { setTypography } from "@/mono/lib/styles/mixins";
import {
  ScrollScene,
  UseCanvas,
  useTracker,
} from "@/mono/third-party/r3f-scroll-rig-custom";
import useResize from "mono/lib/hooks/useResize";
import { clamp } from "mono/lib/math";
import { mediaTabletDown, mediaTabletOnly } from "mono/lib/responsive";
import { useGlobalStore } from "mono/store/global";
import { useCallback, useRef } from "react";
import styled from "styled-components";
import { create } from "zustand";
import { SectionTitle } from "../../SectionTitle";
import HowWeDoItScene, {
  howWeDoItProgress,
  howWeDoItScale,
  howWeDoItX,
} from "../../gl/HowWeDoItScene";

const data = DATA.pages.home.howWeDoIt.cards;

export const HOME_HOW_WE_DO_IT_IN = "home-how-we-do-it:in";
export const HOME_HOW_WE_DO_IT_OUT = "home-how-we-do-it:out";

export const howWeDoItStore = create((set, get) => ({
  cardTransforms: [],
  addTransform: (transform, i) => {
    set((state) => {
      const existingIndex = state.cardTransforms.findIndex((t) => t.i === i);

      if (existingIndex !== -1) {
        // Update existing card
        const updatedTransforms = [...state.cardTransforms];
        updatedTransforms[existingIndex] = { transform, i };
        return { cardTransforms: updatedTransforms };
      } else {
        // Add new card
        return { cardTransforms: [...state.cardTransforms, { transform, i }] };
      }
    });
  },
  getTotalWidth: () => {
    return get().cardTransforms.reduce((acc, t) => {
      return acc + t.transform.scale.x;
    }, 0);
  },
  getWidth: (i) => {
    return get().cardTransforms[i].transform.scale.x;
  },
  getPosition: (i) => {
    return get().cardTransforms[i].transform.position.x;
  },
}));

const Card = ({ children, i }) => {
  const innerRef = useRef();

  const transforms = useTracker(innerRef, {
    autoUpdate: false,
  });

  useIsomorphicLayoutEffect(() => {
    if (transforms.scale.y === 0) return;

    howWeDoItStore.getState().addTransform(transforms, i);
  }, [transforms, i]);

  return (
    <article ref={innerRef} className="how-we-do-it__card">
      {children}
    </article>
  );
};

Card.displayName = "Card";

export function HowWeDoIt() {
  const track = useRef();
  const inner = useRef();

  const _x = useRef(0);
  const _scale = useRef(1);

  const activeItem = useRef();

  const indicatorWrapper = useRef();
  const bounds = useRef();
  const itemBounds = useRef();

  const handleBounds = useCallback(() => {
    if (!indicatorWrapper.current || !activeItem.current) return;

    bounds.current = indicatorWrapper.current.getBoundingClientRect();
    itemBounds.current = activeItem.current.getBoundingClientRect();
  }, [indicatorWrapper.current, activeItem.current]);

  useIsomorphicLayoutEffect(() => {
    handleBounds();
  }, []);

  useResize(handleBounds, { immediate: false });

  const { start, stop } = useTicker(handleMobileTransform, {
    initiallyActive: false,
  });

  // TODO:
  useScrollRigScrollTrigger({
    el: track,
    handleIn: () => {
      start();
      emitter.emit(HOME_HOW_WE_DO_IT_IN);
    },
    handleOut: () => {
      stop();
      emitter.emit(HOME_HOW_WE_DO_IT_OUT);
    },
  });

  function handleMobileTransform() {
    if (!inner.current) return;

    inner.current.style.transform = `scale(${_scale.current}) translateX(${_x.current}px)`;

    let progress = howWeDoItProgress.getState();

    if (activeItem.current && bounds.current?.width > 0) {
      const indicatorWidth = itemBounds.current.width;
      const maxPosition = bounds.current.width - indicatorWidth;

      // FIXME: Hack, slightly boost the progress only when we're near the end
      // otherwise for some reason the final item never gets reached
      if (progress > 0.95) {
        progress = clamp(progress * 1.05, 0, 1);
      }

      const indicatorX = maxPosition * progress;

      activeItem.current.style.transform = `translateX(${indicatorX}px)`;
    }
  }

  useTicker(
    ({ delta }) => {
      if (!useGlobalStore.getState().isTabletOrSmallerLayout) return;

      const x = howWeDoItX.getState();
      const scale = howWeDoItScale.getState();
      _scale.current = lerp(scale, _scale.current, 0.9, delta);
      _x.current = lerp(x, _x.current, 0.1, delta);
    },
    { initiallyActive: true }
  );

  return (
    <>
      <SectionTitle>How we do it</SectionTitle>

      <Wrapper
        ref={track}
        className="how-we-do-it__wrapper"
        style={{ touchAction: "pan-x", overscrollBehaviorY: "contain" }}
      >
        <div className="how-we-do-it__card__inner" ref={inner}>
          {data.map((card, i) => (
            <Card card={card} i={i} key={i.toString()}>
              <h3>0{i + 1}</h3>
              <h4>{card.title}</h4>
              <p className="how-we-do-it__card__text-top">{card.textTop}</p>
              {card.textBottom && <p>{card.textBottom}</p>}
            </Card>
          ))}
        </div>
        <UseCanvas>
          <ScrollScene track={track}>
            {(props) => <HowWeDoItScene {...props} />}
          </ScrollScene>
        </UseCanvas>
        <MobileScrollIndicator ref={indicatorWrapper}>
          {data.map((_, i) => (
            <div
              key={i}
              className="how-we-do-it__card__mobile-indicator-item"
            />
          ))}
          <div
            ref={activeItem}
            className="how-we-do-it__card__mobile-indicator-item active"
          />
        </MobileScrollIndicator>
      </Wrapper>

      <SectionTitle />
    </>
  );
}

const mobileOffset = "50rem";
const mobileMarginBottom = "100rem";

const Wrapper = styled.section`
  position: relative;
  width: 100%;

  ${util.inner}

  padding-top: 100rem;
  padding-bottom: 100rem;

  --cardGap: 20rem;

  touch-action: none;

  .how-we-do-it__card__inner {
    display: flex;
    justify-content: space-between;
    align-items: stretch; // Change this from 'center' to 'stretch'
    width: 100%;
  }

  article {
    width: calc(100% / ${data.length} - (var(--cardGap) * 2));
    display: flex;
    flex-direction: column;
    justify-content: flex-start; // Change this from 'center' to 'flex-start'
    align-items: flex-start;
    flex: 1 1 0; // Add this line to make all cards grow equally
    min-height: 0; // Remove or comment out the min-height property
    padding: 100rem;
    &,
    * {
      user-select: none;
    }

    h3 {
      margin-bottom: 50rem;

      ${({ theme }) => setTypography("glassCardNumber", theme.locale)}
    }

    h4 {
      margin-bottom: 60rem;

      ${({ theme }) => setTypography("glassCardTitle", theme.locale)}
    }

    p {
      ${({ theme }) => setTypography("glassCardParagraph", theme.locale)}
    }

    .how-we-do-it__card__text-top {
      margin-bottom: 40rem;
    }
  }

  ${mediaTabletDown(`
    justify-content: flex-start;

    article {
      width: 100vw;
      min-width: calc(100vw - ${mobilePagePadding} * 2 - (${mobileOffset} * 4));
      padding-top: 240rem;
    }
    
    
    padding-bottom: 300rem;

    --cardGap: 80rem;
  `)}

  .how-we-do-it__card__inner {
    gap: var(--cardGap);
  }
`;

const MobileScrollIndicator = styled.div`
  --size: 40rem;
  position: absolute;

  left: 50%;
  transform: translateX(-50%);
  bottom: calc(250rem * 0.5 - var(--size));
  width: 50%;
  height: var(--size);

  justify-content: space-between;
  display: none;

  .how-we-do-it__card__mobile-indicator-item {
    width: var(--size);
    height: 100%;
    border: 1px solid ${({ theme }) => theme.theme.textColor};
    border-radius: ${misc.borderRadius};

    &.active {
      background-color: ${({ theme }) => theme.theme.textColor};
      position: absolute;
      top: 0;
      left: 0;
    }
  }

  ${mediaTabletDown(`
    display: flex;
  `)}
`;
